import axios from 'axios';
import { API_URLS, BASE_URL, getPlayerImageUrl, getTeamImageUrl, resolveMediaUrl } from '../constants/config';
import { champFields, extractEntity, extractList } from './tournamentsApi';
import { teamFrom, teamCardName } from './matchStaffApi';
import { normalizeMatchWishes } from './schedulerApi';
import {
  DEFAULT_MATCH_TZ,
  kickoffFieldsForApi,
  parseUtcKickoffFields,
  resolveMatchTimezone,
  utcKickoffToWall,
  wallKickoffToUtc,
} from './matchKickoff';

export {
  DEFAULT_MATCH_TZ,
  kickoffFieldsForApi,
  parseUtcKickoffFields,
  resolveMatchTimezone,
  utcKickoffToWall,
  wallKickoffToUtc,
} from './matchKickoff';

export const MATCHES_PAGE_SIZE = 40;

/**
 * Дефолты матча из кэша лиги (getAdminLeague → league_info / top-level cache_*).
 * Нет строки в league_info → поля null, форму не трогаем.
 */
export const extractLeagueMatchDefaults = (league = {}) => {
  const empty = {
    stadiumId: null,
    refereeId: null,
    halfTime: null,
    extraHalfTime: null,
    extraTimeAvailable: null,
    penaltiesAvailable: null,
    timelineEnabled: null,
  };
  if (!league || typeof league !== 'object') return empty;

  const info = league.league_info && typeof league.league_info === 'object'
    ? league.league_info
    : {};
  // league_info перекрывает top-level (актуальный кэш с бэка)
  const src = { ...league, ...info };

  const stadium = src.cache_stadium || src.cacheStadium || null;
  const refs = Array.isArray(src.cache_ref)
    ? src.cache_ref
    : (Array.isArray(src.cacheRef) ? src.cacheRef : []);
  const halfRaw = src.cache_half_time
    ?? src.cache_halfTime
    ?? src.default_half_time
    ?? src.half_time_default
    ?? null;
  const extraHalfRaw = src.cache_extra_half_time ?? src.cache_extraHalfTime ?? null;
  const halfNum = Number(halfRaw);
  const extraHalfNum = Number(extraHalfRaw);

  const hasFlag = (key) => Object.prototype.hasOwnProperty.call(src, key)
    || Object.prototype.hasOwnProperty.call(info, key);

  return {
    stadiumId: Number(stadium?.id || stadium?.value) || null,
    refereeId: Number(refs[0]?.id || refs[0]?.value) || null,
    halfTime: Number.isFinite(halfNum) && halfNum > 0 ? String(halfNum) : null,
    extraHalfTime: Number.isFinite(extraHalfNum) && extraHalfNum > 0 ? String(extraHalfNum) : null,
    extraTimeAvailable: !hasFlag('cache_extra_time_available') || src.cache_extra_time_available == null
      ? null
      : Boolean(src.cache_extra_time_available),
    penaltiesAvailable: !hasFlag('cache_penalty_kicks_available') || src.cache_penalty_kicks_available == null
      ? null
      : Boolean(src.cache_penalty_kicks_available),
    timelineEnabled: !hasFlag('cache_timeline_disabled') || src.cache_timeline_disabled == null
      ? null
      : !src.cache_timeline_disabled,
  };
};

/** Сплющить cache_* из league_info на объект лиги (для selectedLeague в форме). */
export const mergeLeagueInfoCache = (league = {}) => {
  if (!league || typeof league !== 'object') return league;
  const info = league.league_info && typeof league.league_info === 'object'
    ? league.league_info
    : null;
  if (!info) return league;
  const cacheKeys = [
    'cache_half_time',
    'cache_extra_half_time',
    'cache_extra_time_available',
    'cache_penalty_kicks_available',
    'cache_timeline_disabled',
    'cache_stadium',
    'cache_ref',
  ];
  const next = { ...league };
  cacheKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(info, key)) next[key] = info[key];
  });
  return next;
};

// needsPlayer — обязателен только для замены (15). Гол/ЖК/КК/автогол/удары и т.п. можно без player_id.
// Передача (2) в пикер не входит; на бэке для неё игрок тоже обязателен, если когда-то придёт отдельно.
export const MATCH_EVENT_TYPES = [
  { id: 1, event: 'Гол', event_name: 'goal', needsSecond: true, secondLabelKey: 'assist' },
  { id: 3, event: 'Жёлтая карточка', event_name: 'yellow_card' },
  { id: 4, event: 'Удаление', event_name: 'red_card' },
  { id: 6, event: 'Забитый пенальти', event_name: 'penalty_kick_goal' },
  { id: 7, event: 'Незабитый пенальти', event_name: 'penalty_kick_no_goal' },
  { id: 8, event: 'Автогол', event_name: 'autogoal' },
  { id: 13, event: 'Вторая желтая карточка', event_name: 'second_yellow_card' },
  { id: 15, event: 'Замена', event_name: 'substitution', needsPlayer: true, needsSecond: true, secondLabelKey: 'gmPlayerIn' },
  { id: 16, event: 'Забитый 10м', event_name: 'ten_meters_goal' },
  { id: 17, event: 'Незабитый 10м', event_name: 'ten_meters_nogoal' },
  { id: 18, event: 'Забитый 6м пенальти', event_name: 'six_meters_penalty_goal' },
  { id: 19, event: 'Незабитый 6м пенальти', event_name: 'six_meters_penalty_nogoal' },
  { id: 21, event: 'Свободное событие', event_name: 'free_event' },
  { id: 22, event: 'Штанга', event_name: 'shtanga' },
  { id: 23, event: 'Перекладина', event_name: 'perekladina' },
  { id: 121, event: 'Удар по воротам', event_name: 'shot' },
  { id: 122, event: 'Промах', event_name: 'miss' },
  { id: 123, event: 'Угловой', event_name: 'corner' },
  { id: 124, event: 'Опасный момент', event_name: 'dangerous_moment' },
  { id: 125, event: 'Нарушение', event_name: 'foul' },
];

// Подписи для ленты: служебные и пас не должны попадать в пикер.
export const MATCH_EVENT_LABELS = [
  { id: 2, event: 'Передача', event_name: 'assist' },
  { id: 9, event: 'Матч начался', event_name: 'match_begin' },
  { id: 10, event: 'Перерыв', event_name: 'timeout' },
  { id: 11, event: 'Матч окончен', event_name: 'match_end' },
  { id: 14, event: 'Конец основного времени', event_name: 'main_time_end' },
];

export const MAIN_TIMELINE_EVENT_IDS = [1, 3, 4, 6, 8, 13, 16, 18];

export const YELLOW_CARD_EVENT_ID = 3;
export const SECOND_YELLOW_CARD_EVENT_ID = 13;

/** Жёлтые игрока в матче: обычная (3) и вторая (13) считаются как уже выданные ЖК. */
export const countPlayerYellowCards = (events, playerId) => {
  const pid = Number(playerId);
  if (!pid) return 0;
  return (events || []).filter((event) => {
    if (Number(event.player_id) !== pid) return false;
    if (event.isSystem || event.synthetic) return false;
    const eid = Number(event.event_id ?? event.event_type);
    return eid === YELLOW_CARD_EVENT_ID || eid === SECOND_YELLOW_CARD_EVENT_ID;
  }).length;
};

/**
 * Вторая жёлтая тому же игроку → отдельное событие 13.
 * Только при создании (не при редактировании) и только если выбрана обычная ЖК (3).
 */
export const resolveYellowCardEventId = (eventId, events, playerId, { isEdit = false } = {}) => {
  const eid = Number(eventId);
  if (isEdit || eid !== YELLOW_CARD_EVENT_ID || !playerId) return eid;
  return countPlayerYellowCards(events, playerId) >= 1
    ? SECOND_YELLOW_CARD_EVENT_ID
    : YELLOW_CARD_EVENT_ID;
};

export const VIDEO_TYPES = [
  { id: 1, labelKey: 'mmBroadcast' },
  { id: 2, labelKey: 'mmReview' },
  { id: 3, labelKey: 'mmExtra' }, 
];

const EVENT_ICONS = {
  1: '⚽',
  2: '🎯',
  3: '🟨',
  4: '🟥',
  5: '🥅',
  6: '⚽🥅',
  7: '❌🥅',
  8: '⚽🔴',
  9: '▶',
  10: '⏸',
  11: '🏁',
  12: '🧤',
  13: '🟨🟨',
  14: '⏱',
  15: '🔄',
  16: '⚽🔟',
  17: '❌🔟',
  18: '⚽6️⃣',
  19: '❌6️⃣',
  20: '⚽🎯',
  21: '💬',
  22: '|',
  23: '—',
  121: '🎯',
  122: '💨',
  123: '🚩',
  124: '⚠',
  125: '🚫',
};

const EVENT_ICON_FILES = {
  1: { src: '/images/icons/goal.png', invert: true },
  2: { src: '/images/icons/butsa.png' },
  3: { src: '/images/icons/yc.png' },
  4: { src: '/images/icons/rc.png' },
  6: { src: '/images/icons/penalty.png', invert: true },
  7: { src: '/images/icons/nogoal.png' },
  8: { src: '/images/icons/autogoal.png' },
  13: { src: '/images/icons/secondyc.png' },
  15: { src: '/images/icons/substitute.jpg' },
  16: { src: '/images/icons/penalty.png', invert: true },
  17: { src: '/images/icons/nogoal.png' },
  18: { src: '/images/icons/penalty.png', invert: true },
  19: { src: '/images/icons/nogoal.png' },
  20: { src: '/images/icons/goal.png', invert: true },
  121: { src: '/images/icons/icon-soccer-shoe.svg', invert: true },
  122: { src: '/images/icons/no_goal.png' },
  125: { src: '/images/icons/whistle.png' },
};

export const eventIconMeta = (id) => EVENT_ICON_FILES[Number(id)] || null;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const unwrap = (data) => {
  if (data && typeof data === 'object' && data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    if (data.matches || data.staff || data.events || data.statistics) return data;
    return data.data;
  }
  return data && typeof data === 'object' ? data : {};
};

export const eventTypeById = (id) => (
  MATCH_EVENT_TYPES.find((item) => item.id === Number(id))
  || MATCH_EVENT_LABELS.find((item) => item.id === Number(id))
  || null
);

export const eventIcon = (id) => EVENT_ICONS[Number(id)] || '•';

export const eventLabel = (idOrEvent, t) => {
  const event = idOrEvent && typeof idOrEvent === 'object' ? idOrEvent : { event_id: idOrEvent };
  const id = Number(event.event_id ?? event.id);
  const type = eventTypeById(id);
  const nameKey = event.event_name || type?.event_name;
  if (nameKey) {
    const key = `evt_${nameKey}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  if (event.event) return event.event;
  if (type) return type.event;
  return Number.isFinite(id) ? `#${id}` : '—';
};

export const formatClock = (time) => matchClock(time) || '—';

export const matchClock = (time) => {
  const value = String(time || '').trim();
  if (!value || value === '—') return '';
  const parts = value.split(':');
  if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  return value.slice(0, 5);
};

export const formatMatchDate = (date) => {
  const value = String(date || '').slice(0, 10);
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value || '—';
  return `${match[3]}.${match[2]}.${match[1]}`;
};

export const formatKickoff = (match) => {
  let dateValue = match?.date;
  let timeValue = match?.time;
  // normalizeAdminMatch уже кладёт стену в date/time и UTC в *_utc.
  // Сырой ответ API (страница стадии и т.п.) отдаёт UTC naive — переводим здесь.
  const alreadyWall = match?.date_utc != null || match?.time_utc != null;
  if (!alreadyWall && dateValue && timeValue) {
    const wall = utcKickoffToWall(
      String(dateValue).slice(0, 10),
      timeValue,
      resolveMatchTimezone(
        match?.venue_timezone,
        match,
        match?.stadium,
        match?.league,
        match?.championship,
        match?.tournament,
        match?.city_name,
        match?.country_name,
      ),
    );
    if (wall) {
      dateValue = wall.date;
      timeValue = wall.time;
    }
  }
  const date = formatMatchDate(dateValue);
  const time = matchClock(timeValue);
  if ((!date || date === '—') && !time) return '—';
  if (!date || date === '—') return time;
  if (!time) return date;
  return `${date} ${time}`;
};

export const formatEventClock = (time) => String(time || '00:00:00').slice(0, 5);

export const kickoffToDateTimeLocal = (date, time) => {
  const day = String(date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  return `${day}T${matchClock(time) || '00:00'}`;
};

export const dateTimeLocalToKickoff = (value) => {
  const [date, rest] = String(value || '').split('T');
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(date || '') ? date : '',
    time: matchClock(rest),
  };
};

export const MOSCOW_TZ = DEFAULT_MATCH_TZ;
export const MOSCOW_OFFSET = '+03:00';

export const moscowTodayIso = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MOSCOW_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

export const parseMoscowDateTime = (value) => {
  if (value == null || value === '' || value === false || value === 0 || value === '0') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const withOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)
    ? normalized
    : `${normalized}${MOSCOW_OFFSET}`;
  const date = new Date(withOffset);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const elapsedSecondsSince = (value) => {
  const started = parseMoscowDateTime(value);
  if (!started) return 0;
  return Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));
};

export const parseEventClock = (time) => {
  const parts = String(time || '').split(':').map((part) => Number(part) || 0);
  if (parts.length >= 3) {
    const hours = parts[0];
    const minute = (hours > 0 ? hours * 60 : 0) + parts[1];
    return { minute, second: parts[2] };
  }
  if (parts.length === 2) {
    if (parts[0] === 0) return { minute: parts[1], second: 0 };
    return { minute: parts[0], second: parts[1] };
  }
  const asNumber = Number(time);
  if (Number.isFinite(asNumber)) return { minute: asNumber, second: 0 };
  return { minute: 0, second: 0 };
};

/**
 * UI и запись в API — абсолютные минуты матча целиком (36' во 2-м тайме → 00:36:xx).
 * time_number только метка тайма (1/2/3/4). Сервер time не нормализует.
 *
 * Ранее (окт 2026) сюда ошибочно добавили вычитание half_time «под API» —
 * из‑за этого во 2-м тайме уходили относительные 00:12:xx + tn:2.
 */
export const toEventTime = (minute, second, halfTime, extraHalfTime = 15) => {
  const min = Math.max(0, Number(minute) || 0);
  const sec = Math.max(0, Math.min(59, Number(second) || 0));
  const half = Math.max(0, Number(halfTime) || 0);
  const extra = Math.max(1, Number(extraHalfTime) || 15);
  let time_number = 1;
  if (half > 0) {
    if (min >= half * 2 + extra) time_number = 4;
    else if (min >= half * 2) time_number = 3;
    else if (min > half) time_number = 2;
  }
  return {
    time: `00:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`,
    time_number,
    minute: min,
    second: sec,
  };
};

/**
 * Абсолютная минута матча из сырого time + time_number (для редактора / сортировки).
 * Абсолютные записи — как есть; «короткие» relative 2+/extra (баг писателя) разворачиваем.
 */
export const absoluteEventMinute = (time, timeNumber, halfTime, extraHalfTime = 15) => {
  const clock = parseEventClock(time);
  const half = Math.max(0, Number(halfTime) || 0);
  const extra = Math.max(1, Number(extraHalfTime) || 15);
  const tn = Number(timeNumber) || (half > 0 && clock.minute > half ? 2 : 1);
  if (half <= 0) return clock.minute;
  if (tn === 2 && clock.minute < half) return clock.minute + half;
  if (tn === 3 && clock.minute < extra) return clock.minute + half * 2;
  if (tn === 4 && clock.minute < extra) return clock.minute + half * 2 + extra;
  return clock.minute;
};

/**
 * Подпись минуты в таймлайне (футбольный стиль):
 * 00:00:01 → 1', 00:32:00 → 32', 00:32:50 → 33'.
 * В БД по-прежнему абсолютный time, поле minute в редакторе не трогаем.
 */
export const formatEventMinuteLabel = (minute, second = 0) => {
  const min = Math.max(0, Number(minute) || 0);
  const sec = Math.max(0, Number(second) || 0);
  const total = min * 60 + sec;
  if (total <= 0) return 0;
  return Math.ceil(total / 60);
};

export const eventCommentText = (item = {}) => {
  const raw = item.comment ?? item.note;
  if (raw == null) return '';
  return String(raw).trim();
};

export const matchStatusValue = (match) => (
  match?.status == null || match?.status === '' ? null : Number(match.status)
);

export const matchExtraMarkers = (match) => {
  const settings = match?.settings && typeof match.settings === 'object' ? match.settings : {};
  const extraStartRaw = settings.extra_time_start_in ?? match?.extra_time_start_in;
  const extraSecondRaw = settings.extra_second_time_start ?? match?.extra_second_time_start;
  return {
    extraStart: extraStartRaw != null && extraStartRaw !== '' && extraStartRaw !== false,
    extraSecond: extraSecondRaw != null && extraSecondRaw !== '' && extraSecondRaw !== false,
    extraStartAt: extraStartRaw || null,
    extraSecondAt: extraSecondRaw || null,
  };
};

export const matchPlayPhase = (match) => {
  const extra = match?.extra_data || {};
  if (extra.rescheduled || extra.resheduled || match?.rescheduled || match?.resheduled) return 'postponed';
  const status = matchStatusValue(match);
  const { extraStart, extraSecond } = matchExtraMarkers(match);
  if (status === 5) return 'tech_defeat';
  if (status === 2) return extraStart || extraSecond ? 'extra_ended' : 'finished';
  if (status === 4) return extraSecond ? 'extra_second' : 'extra_first';
  if (status === 3) return extraStart ? 'extra_timeout' : 'extra_break';
  if (status === 1) return match?.second_time_start ? 'second_half' : 'first_half';
  if (match?.start_in) return 'half_time';
  return 'not_started';
};

/**
 * Оценка минут:секунд события по серверным меткам (start_in / second_time_start / extra_*).
 * Без пауз — если нужен точный таймер с паузами, используйте локальный онлайн-таймер.
 * Возвращает null, если матч не в «идущем» тайме или меток нет.
 */
export const estimateLiveEventClock = (match) => {
  if (!match) return null;
  const phase = matchPlayPhase(match);
  const halfTime = Number(match.half_time) || 30;
  const extraHalf = Number(
    match?.settings?.extra_half_time
    ?? match?.extra_half_time
    ?? 15,
  );
  const { extraStartAt, extraSecondAt } = matchExtraMarkers(match);
  const fullTimeSec = halfTime * 2 * 60;
  const extraHalfSec = extraHalf * 60;

  let totalSec = null;
  switch (phase) {
    case 'first_half':
      if (!match.start_in) return null;
      totalSec = elapsedSecondsSince(match.start_in);
      break;
    case 'second_half':
      if (!match.second_time_start) return null;
      totalSec = halfTime * 60 + elapsedSecondsSince(match.second_time_start);
      break;
    case 'extra_first':
      if (!extraStartAt) return null;
      totalSec = fullTimeSec + elapsedSecondsSince(extraStartAt);
      break;
    case 'extra_second':
      if (!extraSecondAt) return null;
      totalSec = fullTimeSec + extraHalfSec + elapsedSecondsSince(extraSecondAt);
      break;
    default:
      return null;
  }

  const total = Math.max(0, Math.floor(Number(totalSec) || 0));
  return {
    minute: String(Math.floor(total / 60)),
    second: String(total % 60).padStart(2, '0'),
  };
};

export const matchStatusKey = (match) => {
  const extra = match?.extra_data || {};
  if (extra.rescheduled || extra.resheduled || match?.rescheduled || match?.resheduled) return 'matchPostponed';
  const phase = matchPlayPhase(match);
  const labels = {
    postponed: 'matchPostponed',
    tech_defeat: 'technicalDefeat',
    extra_ended: 'matchFinished',
    finished: 'matchFinished',
    extra_second: 'extraSecondHalf',
    extra_first: 'extraFirstHalf',
    extra_timeout: 'extraHalfTime',
    extra_break: 'extraBreak',
    second_half: 'secondHalf',
    first_half: 'firstHalf',
    half_time: 'halfTime',
    not_started: 'matchNotStarted',
  };
  return labels[phase] || 'matchNotStarted';
};

export const MATCH_PLAY_OPTIONS = [
  { value: 'not_started', condition: 'reset', labelKey: 'matchNotStarted' },
  { value: 'first_half', condition: 'start', labelKey: 'firstHalf' },
  { value: 'half_time', condition: 'timeout', labelKey: 'halfTime' },
  { value: 'second_half', condition: 'continue', labelKey: 'secondHalf' },
  { value: 'extra_break', condition: 'timeout_before_extra', labelKey: 'extraBreak', extraOnly: true },
  { value: 'extra_first', condition: 'extra_first_time', labelKey: 'extraFirstHalf', extraOnly: true },
  { value: 'extra_timeout', condition: 'extra_time_timeout', labelKey: 'extraHalfTime', extraOnly: true },
  { value: 'extra_second', condition: 'extra_second_time', labelKey: 'extraSecondHalf', extraOnly: true },
  { value: 'finished', condition: 'end', labelKey: 'matchFinished' },
  { value: 'tech_defeat', condition: 'tech_defeat', labelKey: 'technicalDefeat' },
];

export const playPhaseSelectValue = (phase) => (
  phase === 'extra_ended' ? 'finished' : phase
);

export const isOnFlag = (value) => value === true || value === 1 || value === '1';

export const isFromCaptain = (value) => {
  if (value && typeof value === 'object') return isOnFlag(value.from_captain);
  return isOnFlag(value);
};

const lineupRowForTeam = (lineups, teamId) => {
  if (!lineups || teamId == null) return null;
  if (Array.isArray(lineups)) {
    return lineups.find((item) => Number(item?.team_id) === Number(teamId)) || null;
  }
  return lineups[String(teamId)] || lineups[Number(teamId)] || null;
};

export const teamHasUnapprovedCaptainLineup = ({ match, roster = [], lineups, teamId } = {}) => {
  if (!teamId) return false;
  const home = Number(teamId) === Number(match?.home_team_id);
  if (home ? match?.has_home_lineup : match?.has_guest_lineup) return false;
  if (home ? match?.has_home_cap_lineup : match?.has_guest_cap_lineup) return true;
  if (isFromCaptain(lineupRowForTeam(lineups || match?.lineups, teamId))) return true;
  return (roster || []).some((player) => (
    player.active
    && isFromCaptain(player)
    && (Number(player.team_id) === Number(teamId) || Number(player.isDouble) === Number(teamId))
  ));
};

export const matchHasUnapprovedCaptainLineup = ({ match, roster, lineups } = {}) => (
  teamHasUnapprovedCaptainLineup({ match, roster, lineups, teamId: match?.home_team_id })
  || teamHasUnapprovedCaptainLineup({ match, roster, lineups, teamId: match?.guest_team_id })
);

export const playConditionForPhase = (nextPhase, currentPhase, match) => {
  if (nextPhase === 'finished') return finishPlayCondition(match || { extra_data: {}, settings: {} }, currentPhase);
  const option = MATCH_PLAY_OPTIONS.find((item) => item.value === nextPhase);
  return option?.condition || nextPhase;
};

export const finishPlayCondition = (match, currentPhase = matchPlayPhase(match)) => {
  const extraAvailable = isOnFlag(match?.settings?.extra_time_available ?? match?.extra_time_available);
  const { extraStart, extraSecond } = matchExtraMarkers(match);
  if (
    ['extra_break', 'extra_first', 'extra_timeout', 'extra_second', 'extra_ended'].includes(currentPhase)
    || extraStart
    || extraSecond
    || extraAvailable
  ) {
    return 'extra_time_end';
  }
  return 'end';
};

export const scoreText = (match) => {
  const status = Number(match?.status);
  const started = status > 0 || match?.second_time_start;
  if (!started && (match?.home_team_scores == null || match?.home_team_scores === '')) return '-:-';
  const home = match?.home_team_scores;
  const guest = match?.guest_team_scores;
  if (home == null && guest == null) return '-:-';
  return `${home ?? 0}:${guest ?? 0}`;
};

export const playerDisplayName = (player) => {
  if (!player) return '';
  return [player.family, player.name].filter(Boolean).join(' ').trim() || player.login || (player.id ? `#${player.id}` : '');
};

export const playerShortName = (player) => {
  if (!player) return '';
  const family = String(player.family || player.player_family || '').trim();
  const name = String(player.name || player.given_name || '').trim();
  if (family && name) return `${family} ${name.charAt(0).toUpperCase()}.`;
  return family || name || String(player.login || '').trim();
};

export const playerShirtNumber = (player) => {
  const value = player?.match_number ?? player?.player_number ?? player?.number;
  if (value == null || value === '') return '';
  return String(value);
};

export const playerEventParts = (player) => ({
  name: playerShortName(player) || playerDisplayName(player),
  number: playerShirtNumber(player),
});

export const teamAbbrev = (team, fallback = '') => {
  const source = String(team?.short_name || team?.name || fallback || '').trim();
  return source ? source.slice(0, 3).toUpperCase() : '';
};

export const playerPhotoUrl = (player) => getPlayerImageUrl(player?.image || player?.photo);

export const teamLogoUrl = (team) => getTeamImageUrl(team?.image || team?.logo);

export const normalizeMatchStatistics = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const statisticId = toNumber(row?.statistic_id);
      if (!statisticId) return null;
      return {
        statistic_id: statisticId,
        name: row.name || row.statistic_name || row.event || '',
        home_team_value: row.home_team_value ?? row.home_value ?? '',
        guest_team_value: row.guest_team_value ?? row.guest_value ?? '',
      };
    })
    .filter(Boolean)
);

export const extractMatchStatistics = (data) => {
  if (Array.isArray(data)) return normalizeMatchStatistics(data);
  const payload = unwrap(data);
  if (Array.isArray(payload.statistics)) return normalizeMatchStatistics(payload.statistics);
  if (Array.isArray(payload.match?.statistics)) return normalizeMatchStatistics(payload.match.statistics);
  if (Array.isArray(payload.data)) return normalizeMatchStatistics(payload.data);
  return [];
};

export const MATCH_KEEPER_POSITION = 4;

const isTruthyFlag = (value) => value === true || value === 1 || value === '1';
const isFalseyFlag = (value) => value === false || value === 0 || value === '0';

const numericPosition = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const ampulaLabel = (value) => {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text || /^\d+$/.test(text)) return '';
  return text;
};

// Вратарь матча живёт в matches_application.position (4), это не амплуа игрока.
export const isMatchKeeper = (player) => Number(player?.match_position) === MATCH_KEEPER_POSITION;

export const matchKeeperPositionValue = (player) => (
  isMatchKeeper(player) ? MATCH_KEEPER_POSITION : null
);

const hasExplicitMatchKeeper = (player = {}) => (
  player.is_keeper != null
  || player.keeper != null
  || player.match_position != null
  || player.application_position != null
  || player.match_application_position != null
);

export const resolveMatchKeeperPosition = (player = {}) => {
  if (isTruthyFlag(player.is_keeper) || isTruthyFlag(player.keeper)) return MATCH_KEEPER_POSITION;
  if (isFalseyFlag(player.is_keeper) || isFalseyFlag(player.keeper)) return null;

  const fromMatch = numericPosition(
    player.match_position ?? player.application_position ?? player.match_application_position
  );
  if (fromMatch != null) {
    return fromMatch === MATCH_KEEPER_POSITION ? MATCH_KEEPER_POSITION : null;
  }

  // `position` / `position_id` в составе — амплуа игрока, не вратарь этого матча.
  return null;
};

const resolveApplicationKeeperPosition = (row = {}, fallback) => {
  if (hasExplicitMatchKeeper(row)) return resolveMatchKeeperPosition(row);
  // В строке заявки `position` — matches_application.position, не амплуа.
  if (Object.prototype.hasOwnProperty.call(row, 'position')) {
    const fromApp = numericPosition(row.position);
    if (fromApp != null) return fromApp === MATCH_KEEPER_POSITION ? MATCH_KEEPER_POSITION : null;
    if (row.position == null || row.position === '') return null;
  }
  return fallback ?? null;
};

export const positionLabel = (player, t) => {
  const raw = ampulaLabel(player?.position) || ampulaLabel(player?.position_name);
  if (raw) return raw;
  const id = Number(player?.position_id);
  if (id === 1) return t('posFW');
  if (id === 2) return t('posMF');
  if (id === 3) return t('posDF');
  if (id === 4) return t('posGK');
  return '';
};

const stadiumFrom = (raw, fallbackName) => {
  if (raw && typeof raw === 'object') {
    return {
      id: toNumber(raw.id),
      name: raw.name || raw.stadium_name || fallbackName || '',
      address: raw.address || '',
      image: raw.image || '',
    };
  }
  return {
    id: toNumber(raw),
    name: fallbackName || '',
    address: '',
    image: '',
  };
};

const pitchFrom = (raw, fallbackName) => {
  if (raw && typeof raw === 'object') {
    return {
      id: toNumber(raw.id),
      name: raw.name || raw.pitch_name || fallbackName || '',
      image: raw.image || '',
    };
  }
  const id = toNumber(raw);
  return {
    id,
    name: fallbackName || '',
    image: '',
  };
};

const textOrNull = (value) => {
  const text = String(value ?? '').trim();
  return text || null;
};

const normalizePhotoSet = (raw, fallbackUrl) => {
  const fallback = fallbackUrl || '';
  if (!raw || typeof raw !== 'object') {
    return fallback ? {
      url: fallback,
      album_name: null,
      group_name: null,
      group_id: null,
      owner_id: null,
      album_id: null,
    } : null;
  }
  return {
    url: textOrNull(raw.url) || fallback || '',
    album_name: textOrNull(raw.album_name ?? raw.albumName),
    group_name: textOrNull(raw.group_name ?? raw.groupName),
    group_id: toNumber(raw.group_id ?? raw.groupId),
    owner_id: toNumber(raw.owner_id ?? raw.ownerId),
    album_id: toNumber(raw.album_id ?? raw.albumId),
  };
};

export const extractPhotoSetPayload = (data) => {
  const payload = unwrap(data);
  const photoSetUrl = payload.photoSetUrl == null
    ? (payload.photo_set && payload.photo_set.url) || ''
    : payload.photoSetUrl;
  const url = photoSetUrl == null ? '' : String(photoSetUrl);
  return {
    photoSetUrl: url,
    photo_set: normalizePhotoSet(payload.photo_set, url),
  };
};

/**
 * @param {object} item сырой матч из API
 * @param {object|null} venueContext лига/чемпионат/город со страницы списка (если в матче нет venue_timezone)
 */
export const normalizeAdminMatch = (item = {}, venueContext = null) => {
  const extra = item.extra_data && typeof item.extra_data === 'object' ? item.extra_data : {};
  const home = teamFrom(item.home_team, item.home_team_name, item.home_team_image, item.home_team_short_name);
  const guest = teamFrom(item.guest_team, item.guest_team_name, item.guest_team_image, item.guest_team_short_name);
  const league = item.league && typeof item.league === 'object'
    ? item.league
    : { id: toNumber(item.league_id), name: item.league_name || '' };
  const season = item.season && typeof item.season === 'object'
    ? item.season
    : { id: toNumber(item.season_id), name: item.season_name || '' };
  const stage = item.stage && typeof item.stage === 'object'
    ? item.stage
    : { id: toNumber(item.stage_id), name: item.stage_name || '', short_name: item.short_stage_name || '' };
  const tournament = item.tournament && typeof item.tournament === 'object'
    ? item.tournament
    : { id: toNumber(item.champ_id || item.championat_id), name: item.tournament_name || '' };
  const settings = item.settings && typeof item.settings === 'object' ? item.settings : {};
  const extra_time_start_in = settings.extra_time_start_in ?? item.extra_time_start_in ?? null;
  const extra_second_time_start = settings.extra_second_time_start ?? item.extra_second_time_start ?? null;
  const venueTimezone = resolveMatchTimezone(
    item.venue_timezone,
    item,
    settings,
    item.stadium,
    item.league,
    league,
    item.championship,
    tournament,
    venueContext,
    {
      city: item.city_name || item.city,
      city_ru: item.city_ru,
      city_id: item.city_id,
      timezone: item.city?.timezone,
      name: item.country_name || item.country?.name || league.country_name,
    },
  );
  const parsed = parseUtcKickoffFields(item);
  const rawDate = parsed.date;
  const rawTime = parsed.time;
  // Сервер: date/time в ответе — UTC naive; timezone:"UTC" = хранение;
  // venue_timezone — IANA площадки для стены на UI.
  const wall = rawDate && rawTime ? utcKickoffToWall(rawDate, rawTime, venueTimezone) : null;
  return {
    ...item,
    id: toNumber(item.id ?? item.match_id),
    date: wall?.date || rawDate,
    time: wall?.time || rawTime,
    date_utc: rawDate,
    time_utc: rawTime,
    timezone: venueTimezone,
    venue_timezone: item.venue_timezone || venueTimezone,
    city_id: toNumber(item.city_id || item.city?.id) || null,
    city_name: item.city_name || item.city?.name || item.city_ru || '',
    status: item.status == null || item.status === '' ? null : toNumber(item.status),
    home_team_scores: item.home_team_scores == null ? '' : item.home_team_scores,
    guest_team_scores: item.guest_team_scores == null ? '' : item.guest_team_scores,
    half_time: toNumber(item.half_time) || 30,
    champ_id: toNumber(item.champ_id || item.championat_id || tournament.id),
    championat_id: toNumber(item.championat_id || item.champ_id || tournament.id),
    country_id: toNumber(item.country_id || item.country?.id || league.country_id),
    country_name: item.country_name || item.country?.name || league.country_name || '',
    country_flag: item.country_flag || item.country?.flag || league.country_flag || '',
    no_date: Boolean(item.no_date) || item.date == null || item.date === '',
    league_id: toNumber(item.league_id || league.id),
    league_name: league.name || item.league_name || '',
    season_id: toNumber(item.season_id || season.id),
    season_name: season.name || item.season_name || '',
    stage_id: toNumber(item.stage_id || stage.id),
    stage_name: stage.short_name || stage.name || item.stage_name || '',
    home_team: home,
    guest_team: guest,
    home_team_id: home.id || toNumber(item.home_team_id),
    guest_team_id: guest.id || toNumber(item.guest_team_id),
    stadium: stadiumFrom(item.stadium, item.stadium_name),
    pitch: pitchFrom(item.pitch, item.pitch_name),
    league,
    season,
    stage,
    tournament,
    extra_data: extra,
    resheduled: extra.resheduled || extra.rescheduled || item.resheduled || item.rescheduled || null,
    rescheduled: extra.rescheduled || extra.resheduled || item.rescheduled || item.resheduled || null,
    default_comment: extra.comment || item.default_comment || item.comment || '',
    statistics: normalizeMatchStatistics(item.statistics),
    videos: Array.isArray(item.videos) ? item.videos : [],
    photos: Array.isArray(item.photos) ? item.photos : [],
    staff: Array.isArray(item.staff) ? item.staff : [],
    penalties: item.penalties || null,
    settings: {
      ...settings,
      extra_time_start_in,
      extra_second_time_start,
    },
    extra_time_start_in,
    extra_second_time_start,
    best_players: Array.isArray(item.best_players) ? item.best_players : [],
    has_home_lineup: Boolean(item.has_home_lineup),
    has_guest_lineup: Boolean(item.has_guest_lineup),
    has_home_cap_lineup: Boolean(item.has_home_cap_lineup),
    has_guest_cap_lineup: Boolean(item.has_guest_cap_lineup),
    wishes: normalizeMatchWishes(
      toNumber(item.id ?? item.match_id),
      item.wishes || item.preferences || [],
    ),
    photoSetUrl: item.photoSetUrl || item.photo_set?.url || '',
    photo_set: normalizePhotoSet(item.photo_set, item.photoSetUrl),
    net_position: (() => {
      const raw = item.net_position ?? item.netPosition;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 1 ? n : null;
    })(),
  };
};

/** Playoff stage flag from admin stage payload. */
export const isPlayoffStage = (stage) => (
  stage?.isPlayoff === true
  || Number(stage?.isPlayoff) === 1
  || stage?.is_playoff === true
  || Number(stage?.is_playoff) === 1
);

/** Expected bracket slots from stage name (1/8→8, final→1). Fallback 16. */
export const estimatePlayoffExpectedSlots = (stageName = '', stage = null) => {
  const fromApi = Number(stage?.expected_slots ?? stage?.expectedSlots ?? stage?.slots);
  if (Number.isFinite(fromApi) && fromApi >= 1) return Math.min(64, Math.round(fromApi));
  const name = String(stageName || stage?.name || stage?.short_name || '').toLowerCase();
  if (/1\s*[\/\-]\s*16|1\/16|шестнадцат/.test(name)) return 16;
  if (/1\s*[\/\-]\s*8|1\/8|восьм|round of 16/.test(name)) return 8;
  if (/1\s*[\/\-]\s*4|1\/4|четверть|quarter/.test(name)) return 4;
  if (/1\s*[\/\-]\s*2|1\/2|полуфин|semi/.test(name)) return 2;
  if (/за\s*3|третье|бронз|3rd|third|малый\s*финал/.test(name)) return 1;
  if (/финал|final/.test(name)) return 1;
  return 16;
};

/** Short round label from stage name: 1/16, 1/8, 1/4, 1/2, final, 3rd. */
export const playoffRoundCode = (stageName = '', stage = null) => {
  const name = String(stageName || stage?.name || stage?.short_name || '').toLowerCase();
  if (/1\s*[\/\-]\s*16|1\/16|шестнадцат/.test(name)) return '1/16';
  if (/1\s*[\/\-]\s*8|1\/8|восьм|round of 16/.test(name)) return '1/8';
  if (/1\s*[\/\-]\s*4|1\/4|четверть|quarter/.test(name)) return '1/4';
  if (/1\s*[\/\-]\s*2|1\/2|полуфин|semi/.test(name)) return '1/2';
  if (/за\s*3|третье|бронз|3rd|third|малый\s*финал/.test(name)) return '3rd';
  if (/финал|final/.test(name)) return 'final';
  return '';
};

/**
 * Human label for net_position on a playoff stage.
 * e.g. 1/16-3, 1/8-2, 1/4-1, 1/2-1, Финал.
 */
export const formatPlayoffNetPositionLabel = (stageName, slot, stage = null, lang = 'ru') => {
  const n = Number(slot);
  if (!Number.isFinite(n) || n < 1) return '';
  const code = playoffRoundCode(stageName, stage);
  if (code === 'final') return lang === 'en' ? 'Final' : 'Финал';
  if (code === '3rd') return lang === 'en' ? '3rd place' : 'За 3 место';
  if (code) return `${code}-${n}`;
  return lang === 'en' ? `Position ${n}` : `Позиция ${n}`;
};

/** Short pairing hint: 1–2 → next-1, 3–4 → next-2. */
export const playoffNetPositionPairingHint = (stageName, slotCount, stage = null, lang = 'ru') => {
  const code = playoffRoundCode(stageName, stage);
  const total = Number(slotCount) || 0;
  if (!code || code === 'final' || code === '3rd' || total < 2) return '';
  const nextMap = {
    '1/16': '1/8',
    '1/8': '1/4',
    '1/4': '1/2',
    '1/2': lang === 'en' ? 'Final' : 'Финал',
  };
  const next = nextMap[code];
  if (!next) return '';
  if (code === '1/2') {
    return lang === 'en'
      ? '1/2-1 and 1/2-2 meet in the Final'
      : '1/2-1 и 1/2-2 сходятся в Финал';
  }
  const pairs = Math.floor(total / 2);
  const samples = [];
  for (let i = 0; i < Math.min(pairs, 3); i += 1) {
    const a = i * 2 + 1;
    const b = i * 2 + 2;
    samples.push(`${code}-${a}+${code}-${b} → ${next}-${i + 1}`);
  }
  const more = pairs > 3 ? (lang === 'en' ? '…' : '…') : '';
  return samples.join('; ') + (more ? ` ${more}` : '');
};

export const extractMatchesPage = (data) => {
  const payload = unwrap(data);
  const list = Array.isArray(payload.matches)
    ? payload.matches
    : (Array.isArray(payload) ? payload : extractList(payload));
  const groups = (Array.isArray(payload.groups) ? payload.groups : []).map((group, index) => {
    const matches = (Array.isArray(group.matches) ? group.matches : []).map(normalizeAdminMatch).filter((item) => item.id);
    return {
      country_id: toNumber(group.country_id ?? group.id ?? matches[0]?.country_id) || `group-${index}`,
      country_name: group.country_name || group.name || matches[0]?.country_name || '',
      country_flag: group.country_flag || group.flag || matches[0]?.country_flag || '',
      matches,
    };
  }).filter((group) => group.matches.length);
  const matches = list.map(normalizeAdminMatch).filter((item) => item.id);
  const flattened = matches.length ? matches : groups.reduce((acc, group) => acc.concat(group.matches), []);
  return {
    total: toNumber(payload.total) ?? flattened.length,
    matches: flattened,
    groups,
  };
};

export const extractMatch = (data) => normalizeAdminMatch(extractEntity(data) || unwrap(data));

export const extractRoster = (data) => {
  const payload = unwrap(data);
  const list = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload.players)
      ? payload.players
      : (Array.isArray(payload.roster)
        ? payload.roster
        : (Array.isArray(payload.application) ? payload.application : extractList(payload))));
  return list.map((player) => ({
    ...player,
    // В заявке ma.id — id строки; игрок — player_id. Иначе в событие уходит id заявки и на табло пустое имя.
    id: toNumber(player.player_id ?? player.id),
    team_id: toNumber(player.team_id),
    active: Boolean(player.active),
    player_number: player.player_number == null ? '' : String(player.player_number),
    match_number: player.match_number == null ? (player.player_number == null ? '' : String(player.player_number)) : String(player.match_number),
    goals: player.goals || 0,
    assists: player.assists || 0,
    autogoals: player.autogoals || 0,
    yellow_cards: player.yellow_cards || 0,
    red_cards: player.red_cards || 0,
    mvp: player.mvp || null,
    isDouble: player.isDouble || player.from_double || 0,
    from_captain: isFromCaptain(player),
    match_position: resolveMatchKeeperPosition(player),
  })).filter((player) => player.id);
};

export const normalizeMatchEvent = (item = {}, match, roster = []) => {
  const eventId = toNumber(item.event_id ?? item.event_type ?? item.type);
  const playerId = toNumber(item.player_id);
  const secondId = toNumber(item.additional_player_id ?? item.assist_player_id ?? item.second_player_id);
  const player = roster.find((entry) => entry.id === playerId) || null;
  const second = roster.find((entry) => entry.id === secondId) || null;
  const clock = parseEventClock(item.time || item.minute);
  const homeId = match?.home_team_id;
  const teamId = toNumber(item.team_id);
  const team = teamId === homeId
    ? match?.home_team
    : (teamId === match?.guest_team_id ? match?.guest_team : null);
  const teamName = teamCardName(team) || team?.name || '';
  const halfTime = Number(match?.half_time) || 30;
  const extraHalf = Number(
    match?.settings?.extra_half_time
    ?? match?.extra_half_time
    ?? 15,
  );
  const timeNumber = toNumber(item.time_number) || (clock.minute > halfTime ? 2 : 1);
  // Абсолют — как в БД; relative 2+/короткий time (баг админки окт 2026) → развернуть.
  const minute = absoluteEventMinute(item.time || item.minute, timeNumber, halfTime, extraHalf);
  return {
    id: toNumber(item.id),
    server_id: toNumber(item.id),
    event_id: eventId,
    player_id: playerId,
    additional_player_id: secondId,
    team_id: teamId,
    league_id: toNumber(item.league_id || match?.league_id),
    time: item.time || `00:${String(minute).padStart(2, '0')}:${String(clock.second).padStart(2, '0')}`,
    time_number: timeNumber,
    minute,
    second: clock.second,
    comment: eventCommentText(item),
    event: item.event || '',
    event_name: item.event_name || '',
    player_number: playerShirtNumber(player) || item.player_number || '',
    player_name: playerDisplayName(player) || [item.family || item.player_family, item.name || item.player_name].filter(Boolean).join(' '),
    second_name: second ? playerDisplayName(second) : '',
    player_image: player?.image || item.image || item.player_image || '',
    team_name: teamName,
    team_short: teamAbbrev(team, teamName),
    team_image: team?.image || item.team_image || '',
    additional_event_id: toNumber(item.additional_event_id),
  };
};

const eventClockKey = (event) => {
  const clock = parseEventClock(event?.time);
  const minute = Number.isFinite(Number(event?.minute)) ? Number(event.minute) : clock.minute;
  const second = Number.isFinite(Number(event?.second)) ? Number(event.second) : clock.second;
  return `${Number(event?.time_number) || 0}|${minute}|${second}`;
};

// Отдельная передача (event_id = 2) с тем же временем, что и гол, относится к этому голу.
export const linkAssistsToGoals = (events = [], roster = []) => {
  const list = (events || []).map((event) => ({ ...event }));
  const takenAssists = new Set();
  const takenGoals = new Set();

  list.forEach((assist, assistIndex) => {
    if (Number(assist.event_id) !== 2) return;
    const key = eventClockKey(assist);
    const matches = list
      .map((event, index) => ({ event, index }))
      .filter(({ event, index }) => (
        !takenGoals.has(index)
        && Number(event.event_id) === 1
        && eventClockKey(event) === key
        && Number(event.player_id) !== Number(assist.player_id)
      ));
    const alreadyLinked = matches.find(({ event }) => Number(event.additional_player_id) === Number(assist.player_id));
    const sameTeam = matches.find(({ event }) => (
      !event.additional_player_id
      && Number(event.team_id) === Number(assist.team_id)
    ));
    const openGoal = matches.find(({ event }) => !event.additional_player_id);
    const target = alreadyLinked || sameTeam || openGoal;
    if (!target) return;

    takenAssists.add(assistIndex);
    takenGoals.add(target.index);
    const assistId = assist.id || assist.server_id || null;
    const second = roster.find((player) => Number(player.id) === Number(assist.player_id));
    list[target.index] = {
      ...target.event,
      additional_player_id: target.event.additional_player_id || assist.player_id,
      second_name: target.event.second_name || playerDisplayName(second) || assist.player_name || '',
      additional_event_id: target.event.additional_event_id || assistId,
    };
  });

  return list.filter((_, index) => !takenAssists.has(index));
};

export const extractEvents = (data, match, roster = []) => {
  const payload = unwrap(data);
  const list = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload.events) ? payload.events : extractList(payload));
  return linkAssistsToGoals(
    list.map((item) => normalizeMatchEvent(item, match, roster)).filter((item) => item.event_id),
    roster,
  );
};

export const applicationRow = (matchId, player) => ({
  match_id: matchId,
  team_id: player.team_id,
  player_id: player.id,
  from_double: player.isDouble ? 1 : null,
  position_id: matchKeeperPositionValue(player),
});

export const eventPayloadRow = (match, event) => ({
  match_id: match.id,
  event_id: event.event_id,
  player_id: event.player_id || null,
  team_id: event.team_id || null,
  league_id: match.league_id,
  time: event.time,
  time_number: event.time_number || 0,
  comment: eventCommentText(event) || null,
});

export const MATCH_SERVICE_EVENT_IDS = [9, 10, 11, 14];

/** События, для которых player_id необязателен (как на сервере в parseEventPayload). */
export const OPTIONAL_PLAYER_EVENT_IDS = new Set([21, 22, 23, 121, 122, 123, 124, 125]);

export const isMatchServiceEvent = (event) => MATCH_SERVICE_EVENT_IDS.includes(Number(event?.event_id));

export const isValidRosterEventPayload = (event) => {
  const eventId = Number(event?.event_id);
  if (!Number.isFinite(eventId) || eventId <= 0) return false;
  const playerId = Number(event?.player_id);
  const teamId = Number(event?.team_id);
  const playerOptional = OPTIONAL_PLAYER_EVENT_IDS.has(eventId);
  if (!playerOptional && !(Number.isFinite(playerId) && playerId > 0)) return false;
  if (eventId !== 21 && !(Number.isFinite(teamId) && teamId > 0)) return false;
  return true;
};

export const realMatchEvents = (events) => (events || []).filter((event) => !isMatchServiceEvent(event));

const serviceEvent = (match, eventId, minute) => {
  const clock = toEventTime(minute, 0, match?.half_time);
  return {
    id: null,
    server_id: null,
    synthetic: true,
    event_id: eventId,
    player_id: null,
    additional_player_id: null,
    team_id: null,
    league_id: toNumber(match?.league_id),
    time: clock.time,
    time_number: clock.time_number,
    minute,
    second: 0,
    comment: '',
    player_name: '',
    second_name: '',
    team_name: '',
    additional_event_id: null,
  };
};

const timelineServiceRank = (event) => {
  const id = Number(event?.event_id);
  if (id === 9) return -1;
  if (id === 14) return 2;
  if (id === 10) return 3;
  if (id === 11) return 4;
  return 1;
};

const matchScoresTied = (match) => {
  const home = Number(match?.home_team_scores);
  const guest = Number(match?.guest_team_scores);
  if (!Number.isFinite(home) || !Number.isFinite(guest)) return false;
  return home === guest;
};

export const withMatchTimelineEvents = (events, match) => {
  const real = realMatchEvents(events);
  const phase = matchPlayPhase(match);
  if (phase === 'not_started' || phase === 'postponed') return real;

  const halfTime = Number(match?.half_time) || 30;
  const extraHalf = Number(match?.settings?.extra_half_time || match?.extra_half_time || 15);
  const extraAvailable = isOnFlag(match?.settings?.extra_time_available ?? match?.extra_time_available);
  const { extraStart, extraSecond } = matchExtraMarkers(match);
  const extraBegan = extraStart || extraSecond || ['extra_first', 'extra_timeout', 'extra_second', 'extra_ended'].includes(phase);
  const matchOver = ['finished', 'extra_ended', 'tech_defeat'].includes(phase);
  const regularOver = extraBegan || ['extra_break', 'extra_first', 'extra_timeout', 'extra_second', 'extra_ended', 'finished', 'tech_defeat'].includes(phase);
  const extraNeeded = extraBegan || phase === 'extra_break' || (extraAvailable && matchScoresTied(match) && regularOver);
  const extraBreakReached = extraSecond || ['extra_timeout', 'extra_second', 'extra_ended'].includes(phase)
    || (matchOver && extraNeeded);
  const synthetic = [];

  synthetic.push(serviceEvent(match, 9, 0));

  if (['half_time', 'second_half', 'extra_break', 'extra_first', 'extra_timeout', 'extra_second', 'extra_ended', 'finished', 'tech_defeat'].includes(phase)) {
    synthetic.push(serviceEvent(match, 10, halfTime));
  }

  if (extraNeeded) {
    synthetic.push(serviceEvent(match, 14, halfTime * 2));
  }

  if (extraBreakReached) {
    synthetic.push(serviceEvent(match, 10, halfTime * 2 + extraHalf));
  }

  if (matchOver) {
    let endMinute = halfTime * 2;
    if (extraSecond || (extraNeeded && !extraBegan)) endMinute = halfTime * 2 + extraHalf * 2;
    else if (extraBegan) endMinute = halfTime * 2 + extraHalf;
    synthetic.push(serviceEvent(match, 11, endMinute));
  }

  return [...synthetic, ...real].sort((a, b) => {
    const byMinute = (Number(a.minute) || 0) - (Number(b.minute) || 0);
    if (byMinute) return byMinute;
    const bySecond = (Number(a.second) || 0) - (Number(b.second) || 0);
    if (bySecond) return bySecond;
    return timelineServiceRank(a) - timelineServiceRank(b);
  });
};

const randomInt = (halfTime) => {
  const min = 11;
  const max = Math.max(12, (Number(halfTime) || 30) * 2);
  return Math.floor(Math.random() * (max - min)) + min;
};

const rosterApplicationRow = (player) => ({
  player_id: player.id,
  team_id: player.team_id,
  from_double: player.isDouble ? 1 : null,
  position: matchKeeperPositionValue(player),
  match_number: player.match_number === '' || player.match_number == null ? null : toNumber(player.match_number),
});

const mvpRows = (roster) => roster
  .filter((player) => player.mvp)
  .map((player) => ({ player_id: player.id, team_id: player.team_id }));

const statErrorPlayers = (match, roster) => {
  const teams = {
    [match.home_team_id]: match.home_team?.name,
    [match.guest_team_id]: match.guest_team?.name,
  };
  return roster
    .filter((player) => !player.active && (Number(player.goals) || Number(player.autogoals) || Number(player.assists)
      || Number(player.yellow_cards) || Number(player.red_cards)))
    .map((player) => ({ ...player, team_name: teams[player.team_id] }));
};

// Протокол хранит только суммы за матч, поэтому минуты событий выставляются случайно.
export const buildProtocolRosterEvents = ({ match, roster }) => {
  const halfTime = Number(match.half_time) || 30;
  const events = [];
  const pushTimed = (eventId, player, additionalPlayerId = null) => {
    const minutes = randomInt(halfTime);
    const seconds = randomInt(halfTime) % 60;
    const clock = toEventTime(minutes, seconds, halfTime);
    events.push({
      event_id: eventId,
      player_id: player.id,
      team_id: player.team_id,
      additional_player_id: additionalPlayerId,
      time: clock.time,
      time_number: clock.time_number,
    });
  };

  const counters = roster.map((player) => ({
    ...player,
    goals: Number(player.goals) || 0,
    assists: Number(player.assists) || 0,
    autogoals: Number(player.autogoals) || 0,
    yellow_cards: Number(player.yellow_cards) || 0,
    red_cards: Number(player.red_cards) || 0,
  }));

  counters.forEach((player) => {
    for (let i = 0; i < player.goals; i += 1) {
      const assist = counters.find((item) => item.id !== player.id && item.team_id === player.team_id && item.assists > 0);
      if (assist) assist.assists -= 1;
      pushTimed(1, player, assist ? assist.id : null);
    }
    for (let i = 0; i < player.autogoals; i += 1) pushTimed(8, player);
    for (let i = 0; i < player.assists; i += 1) pushTimed(2, player);
    for (let i = 0; i < player.yellow_cards; i += 1) pushTimed(i % 2 === 1 ? 13 : 3, player);
    for (let i = 0; i < player.red_cards; i += 1) pushTimed(4, player);
  });

  return {
    match_id: match.id,
    application: roster.filter((player) => player.active).map(rosterApplicationRow),
    events,
    mvp: mvpRows(roster),
    errorPlayers: statErrorPlayers(match, roster),
  };
};

export const buildRosterEventsPayload = ({ match, roster, events }) => {
  const real = realMatchEvents(events);
  const assistedIds = new Set(real
    .filter((event) => Number(event.event_id) === 1 && event.additional_player_id)
    .map((event) => Number(event.additional_player_id)));
  return {
    match_id: match.id,
    application: roster.filter((player) => player.active).map(rosterApplicationRow),
    events: real
      .filter((event) => !(Number(event.event_id) === 2 && assistedIds.has(Number(event.player_id))))
      .filter(isValidRosterEventPayload)
      .map((event) => ({
        event_id: Number(event.event_id),
        player_id: event.player_id || null,
        team_id: event.team_id || null,
        additional_player_id: event.additional_player_id || null,
        time: event.time,
        time_number: event.time_number || 0,
        comment: eventCommentText(event) || null,
      })),
    mvp: mvpRows(roster),
  };
};

// Состав + вратари (position: 4) + MVP. Без событий, счёта и завершения матча.
export const buildMatchApplicationPayload = ({ match, roster, teamId = null } = {}) => {
  const scoped = roster.filter((player) => {
    if (!player.active) return false;
    if (teamId == null) return true;
    return Number(player.team_id) === Number(teamId) || Number(player.isDouble) === Number(teamId);
  });
  const application = scoped.map((player) => ({
    player_id: player.id,
    team_id: player.team_id,
    position: matchKeeperPositionValue(player),
    match_number: player.match_number === '' || player.match_number == null ? null : toNumber(player.match_number),
    mvp: player.mvp ? true : null,
  }));
  const body = {
    match_id: match.id,
    application,
    mvp: mvpRows(scoped),
  };
  if (teamId != null) body.team_id = toNumber(teamId);
  return body;
};

export const buildApproveCaptainPayload = ({ match, teamId = null } = {}) => {
  const body = { match_id: match.id, approve_captain: true };
  if (teamId != null) body.team_id = toNumber(teamId);
  return body;
};

// Рут отдаёт объект схем с ключами-строками по id команды.
export const extractLineups = (data) => {
  const payload = unwrap(data);
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  if (payload.lineups && typeof payload.lineups === 'object') return payload.lineups;
  return payload;
};

export const extractRosterEvents = (data, match, roster = []) => {
  const payload = unwrap(data) || {};
  // В строке заявки id — это id самой строки, игрока определяет player_id.
  const rawList = (payload.application || payload.roster || [])
    .map((item) => ({ ...item, id: toNumber(item.player_id ?? item.id) }));
  const rawById = new Map(rawList.map((item) => [item.id, item]));
  const bestIds = new Set((payload.best_players || [])
    .map((item) => toNumber(item.player_id ?? item.id))
    .filter(Boolean));
  // В ответе from_double — это флаг 1/0, а в стейте isDouble хранит команду матча, поэтому берём её из текущего состава.
  const localById = new Map(roster.map((player) => [player.id, player]));
  // Рут отдаёт только заявку, поэтому попавший в ответ игрок считается заявленным.
  const application = extractRoster(rawList).map((player) => {
    const raw = rawById.get(player.id) || {};
    const local = localById.get(player.id);
    return {
      ...player,
      active: raw.active === undefined ? true : Boolean(raw.active),
      mvp: bestIds.has(player.id) || null,
      isDouble: local ? local.isDouble : player.isDouble,
      position: ampulaLabel(player.position) || local?.position || player.position,
      position_id: player.position_id ?? local?.position_id,
      from_captain: raw.from_captain != null ? isFromCaptain(raw) : Boolean(local?.from_captain),
      match_position: resolveApplicationKeeperPosition(raw, local?.match_position),
    };
  });
  return {
    roster: application,
    // Ответ узнан, только если его игроки есть в текущем составе — иначе состав не трогаем.
    recognized: application.some((player) => localById.has(player.id)),
    events: realMatchEvents(extractEvents(payload.events || [], match, application.length ? application : roster)),
  };
};

export const buildProtocolPayload = ({ match, roster, score, stadiumId, date, time, timelineDisabled }) => {
  const events = [];
  const newApplication = [];
  const errorPlayers = [];
  const halfTime = Number(match.half_time) || 30;
  const teams = {
    [match.home_team_id]: match.home_team?.name,
    [match.guest_team_id]: match.guest_team?.name,
  };

  events.push({
    match_id: match.id, event_id: 9, player_id: null, team_id: null,
    league_id: match.league_id, time: '00:00:00', time_number: 1,
  });
  {
    const halfEnd = toEventTime(halfTime, 0, halfTime);
    events.push({
      match_id: match.id, event_id: 10, player_id: null, team_id: null,
      league_id: match.league_id, time: halfEnd.time, time_number: halfEnd.time_number,
    });
  }
  {
    const fullTime = toEventTime(halfTime * 2, 0, halfTime);
    events.push({
      match_id: match.id, event_id: 11, player_id: null, team_id: null,
      league_id: match.league_id, time: fullTime.time, time_number: fullTime.time_number,
    });
  }

  const temp = roster.map((player) => ({
    ...player,
    goals: Number(player.goals) || 0,
    assists: Number(player.assists) || 0,
    autogoals: Number(player.autogoals) || 0,
    yellow_cards: Number(player.yellow_cards) || 0,
    red_cards: Number(player.red_cards) || 0,
  }));

  const pushTimed = (eventId, player) => {
    const minutes = randomInt(halfTime);
    const seconds = randomInt(halfTime) % 60;
    const clock = toEventTime(minutes, seconds, halfTime);
    events.push({
      match_id: match.id,
      event_id: eventId,
      player_id: player.id,
      team_id: player.team_id,
      league_id: match.league_id,
      time: clock.time,
      time_number: clock.time_number,
    });
  };

  roster.forEach((player) => {
    const hasEvents = Number(player.goals) || Number(player.autogoals) || Number(player.assists)
      || Number(player.yellow_cards) || Number(player.red_cards);
    if (!player.active && hasEvents) {
      errorPlayers.push({ ...player, team_name: teams[player.team_id] });
    }
    if (player.active) newApplication.push(applicationRow(match.id, player));
  });

  temp.forEach((player) => {
    for (let i = 0; i < player.goals; i += 1) {
      pushTimed(1, player);
      const assist = temp.find((item) => item.id !== player.id && item.team_id === player.team_id && item.assists > 0);
      if (assist) {
        pushTimed(2, assist);
        assist.assists -= 1;
      }
    }
    for (let i = 0; i < player.autogoals; i += 1) pushTimed(8, player);
    for (let i = 0; i < player.assists; i += 1) pushTimed(2, player);
    for (let i = 0; i < player.yellow_cards; i += 1) pushTimed(i % 2 === 1 ? 13 : 3, player);
    for (let i = 0; i < player.red_cards; i += 1) pushTimed(4, player);
  });

  const matchData = {
    home: score.home === '' || score.home == null ? 0 : Number(score.home),
    guest: score.guest === '' || score.guest == null ? 0 : Number(score.guest),
    date: date || match.date || null,
    time: time || (match.time ? `${match.time}:00` : null),
    stadium_id: stadiumId || match.stadium?.id || null,
    timeline_disabled: timelineDisabled ? 1 : null,
  };

  return { matchApplication: newApplication, matchEvents: events, matchData, errorPlayers };
};

export const photoUrl = (photo) => {
  const raw = photo?.photo || photo?.href || photo?.url || photo?.image || photo?.path;
  if (!raw) return '';
  if (String(raw).startsWith('data:') || String(raw).startsWith('blob:')) return String(raw);
  if (/^https?:\/\//i.test(String(raw))) {
    try {
      const url = new URL(String(raw));
      return resolveMediaUrl(url.pathname + url.search);
    } catch (error) {
      return resolveMediaUrl(String(raw));
    }
  }
  if (String(raw).startsWith('/')) return resolveMediaUrl(raw);
  return resolveMediaUrl(`/${raw}`);
};

export const matchProtocolUrl = (matchId, filled = false) => (
  `${BASE_URL.url}/matches/${matchId}/protocol${filled ? '/1' : ''}`
);

export const openMatchProtocol = (matchId, filled = false) => {
  if (!matchId) return;
  window.open(matchProtocolUrl(matchId, filled), '_blank', 'noopener');
};

export const matchRequestBody = (champId, extra = {}) => ({
  ...champFields(champId),
  ...extra,
});

export const loadMatchEvents = async (dispatch, matchId, match, roster) => {
  const { checkIfTokenExpired, getTokensFromStore } = await import('./tokens');
  const { refreshTokens } = await import('../actions/usersActions');
  if (checkIfTokenExpired()) await dispatch(refreshTokens());
  const tokens = getTokensFromStore();
  const response = await axios.get(`${BASE_URL.url}${API_URLS.getMatch}${matchId}/events`, {
    headers: {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
    },
  });
  return realMatchEvents(extractEvents(response.data, match, roster));
};
